/**
 * TechnicalConfiguratorV2 - Configuratore tecnico impianto (orchestratore)
 *
 * Gestisce stato BOM, calcoli, validazione e composizione delle sezioni:
 * banner validazione, sintesi tecnica, stato compatibilità e blocchi prodotto.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { TooltipProvider } from './ui/tooltip';
import { toast } from 'sonner';
import api from '../lib/api';
import { Sun, Zap, Battery, Loader2, RefreshCw } from 'lucide-react';
import { INVERTER_TYPE_CODES, TYPE_COLORS, evaluateDcAcRatio } from './configurator/constants';
import { ValidationBanner } from './configurator/ValidationBanner';
import { SintesiTecnicaCard } from './configurator/SintesiTecnicaCard';
import { StatoCompatibilitaCard } from './configurator/StatoCompatibilitaCard';
import { ProductSection } from './configurator/ProductSection';

export default function TechnicalConfiguratorV2({
  contractId,
  nodeId,
  readOnly = false,
  onConfigChange,
  onValidationChange
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bom, setBom] = useState({ items: [] });
  const [storageEnabled, setStorageEnabled] = useState(false);

  // Carica BOM
  const loadBOM = useCallback(async () => {
    if (!contractId || !nodeId) return;

    setLoading(true);
    try {
      const response = await api.get(`/catalog/bom/${contractId}/${nodeId}`);
      setBom(response.data);

      // Attiva il toggle storage se esistono item di accumulo
      const hasStorage = response.data.items?.some(i => i.catalog_item?.type_code === 'STORAGE');
      setStorageEnabled(hasStorage);
    } catch (error) {
      console.error('Error loading BOM:', error);
    } finally {
      setLoading(false);
    }
  }, [contractId, nodeId]);

  useEffect(() => {
    loadBOM();
  }, [loadBOM]);

  // ==================== CALCOLI ====================
  const calculations = useMemo(() => {
    let totalModuliPower = 0;
    let totalInverterPower = 0;
    let totalStorageCapacity = 0;
    let totalPrice = 0;
    let moduliCount = 0;
    let inverterCount = 0;
    let storageCount = 0;

    bom.items?.forEach(item => {
      const catalogItem = item.catalog_item;
      if (!catalogItem) return;

      const qty = item.quantity || 0;

      if (catalogItem.prezzo_listino) {
        totalPrice += catalogItem.prezzo_listino * qty;
      }

      if (catalogItem.type_code === 'MODULI' && catalogItem.technical_specs?.pmax_w) {
        totalModuliPower += (catalogItem.technical_specs.pmax_w * qty) / 1000;
        moduliCount += qty;
      }

      if ((catalogItem.type_code === 'INVERTER' || catalogItem.type_code === 'INVERTER_IBRIDO')
          && catalogItem.technical_specs?.potenza_nominale_ac_kw) {
        totalInverterPower += catalogItem.technical_specs.potenza_nominale_ac_kw * qty;
        inverterCount += qty;
      }

      if (catalogItem.type_code === 'STORAGE' && catalogItem.technical_specs?.capacita_nominale_kwh) {
        totalStorageCapacity += catalogItem.technical_specs.capacita_nominale_kwh * qty;
        storageCount += qty;
      }
    });

    const dcAcRatio = totalInverterPower > 0
      ? (totalModuliPower / totalInverterPower).toFixed(2)
      : 0;

    return {
      totalModuliPower: totalModuliPower.toFixed(2),
      totalInverterPower: totalInverterPower.toFixed(2),
      totalStorageCapacity: totalStorageCapacity.toFixed(2),
      dcAcRatio,
      dcAcStatus: evaluateDcAcRatio(dcAcRatio),
      totalPrice,
      moduliCount,
      inverterCount,
      storageCount
    };
  }, [bom.items]);

  // ==================== VALIDAZIONE / AVVISI ====================
  const validation = useMemo(() => {
    const warnings = [];
    const errors = [];

    if (calculations.moduliCount === 0) {
      errors.push({ type: 'error', message: 'Nessun modulo selezionato', field: 'MODULI' });
    }

    if (calculations.inverterCount === 0) {
      errors.push({ type: 'error', message: 'Nessun inverter selezionato', field: 'INVERTER' });
    }

    if (calculations.dcAcStatus.status === 'error' && calculations.inverterCount > 0) {
      errors.push({
        type: 'error',
        message: `Rapporto DC/AC ${calculations.dcAcRatio} - ${calculations.dcAcStatus.message}`,
        field: 'DC_AC'
      });
    } else if (calculations.dcAcStatus.status === 'warning') {
      warnings.push({
        type: 'warning',
        message: `Rapporto DC/AC ${calculations.dcAcRatio} - ${calculations.dcAcStatus.message}`,
        field: 'DC_AC'
      });
    }

    // Storage presente senza inverter ibrido
    const hasStorage = calculations.storageCount > 0;
    const hasHybridInverter = bom.items?.some(i =>
      i.catalog_item?.type_code === 'INVERTER_IBRIDO' ||
      (i.catalog_item?.type_code === 'INVERTER' && i.catalog_item?.technical_specs?.ibrido === true)
    );

    if (hasStorage && !hasHybridInverter) {
      warnings.push({
        type: 'warning',
        message: 'Storage presente ma nessun inverter ibrido selezionato',
        field: 'STORAGE_COMPAT'
      });
    }

    let status = 'valid';
    let statusMessage = 'Configurazione valida';

    if (errors.length > 0) {
      status = 'invalid';
      statusMessage = 'Configurazione non valida';
    } else if (warnings.length > 0) {
      status = 'warning';
      statusMessage = 'Configurazione da verificare';
    }

    const canComplete = errors.length === 0 && calculations.moduliCount > 0 && calculations.inverterCount > 0;

    return { warnings, errors, status, statusMessage, canComplete };
  }, [bom.items, calculations]);

  // Notifica il parent dei cambiamenti
  useEffect(() => {
    if (onConfigChange) {
      onConfigChange({ bom, calculations, validation });
    }
    if (onValidationChange) {
      onValidationChange(validation);
    }
  }, [bom, calculations, validation, onConfigChange, onValidationChange]);

  // ==================== HANDLER ====================

  const handleAddItem = async (catalogItem, quantity) => {
    if (!catalogItem || quantity < 1) return;

    setSaving(true);
    try {
      await api.post(`/catalog/bom/${contractId}/${nodeId}/add-item`, {
        catalog_item_id: catalogItem.id,
        quantity: quantity,
        notes: null
      });
      toast.success('Componente aggiunto');
      loadBOM();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuantity = async (index, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      const newItems = bom.items.map((item, idx) => ({
        catalog_item_id: item.catalog_item?.id || item.catalog_item_id,
        quantity: idx === index ? newQuantity : item.quantity,
        notes: item.notes
      }));

      await api.put(`/catalog/bom/${contractId}/${nodeId}`, { items: newItems });
      loadBOM();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleRemoveItem = async (index) => {
    try {
      await api.delete(`/catalog/bom/${contractId}/${nodeId}/item/${index}`);
      toast.success('Componente rimosso');
      loadBOM();
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  // Item indicizzati con posizione originale nel BOM, filtrati per tipologia
  const indexedItems = useMemo(() =>
    (bom.items || []).map((item, originalIndex) => ({ item, originalIndex })),
  [bom.items]);

  const moduleItems = useMemo(() =>
    indexedItems.filter(({ item }) => item.catalog_item?.type_code === 'MODULI'),
  [indexedItems]);

  const inverterItems = useMemo(() =>
    indexedItems.filter(({ item }) =>
      item.catalog_item?.type_code === 'INVERTER' || item.catalog_item?.type_code === 'INVERTER_IBRIDO'
    ),
  [indexedItems]);

  const storageItems = useMemo(() =>
    indexedItems.filter(({ item }) => item.catalog_item?.type_code === 'STORAGE'),
  [indexedItems]);

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-8" data-testid="technical-configurator">
        <ValidationBanner validation={validation} />

        {/* Sintesi Tecnica + Stato Compatibilità (affiancate) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <SintesiTecnicaCard calculations={calculations} storageEnabled={storageEnabled} />
          <StatoCompatibilitaCard calculations={calculations} validation={validation} storageEnabled={storageEnabled} />
        </div>

        {/* Configuratore a blocchi */}
        <div className="space-y-6">
          <ProductSection
            title="Moduli Fotovoltaici"
            icon={Sun}
            colors={TYPE_COLORS.MODULI}
            badgeClass="bg-amber-100 text-amber-700 border-0"
            count={calculations.moduliCount}
            typeCode="MODULI"
            items={moduleItems}
            onAdd={handleAddItem}
            onUpdateQuantity={handleUpdateQuantity}
            onRemove={handleRemoveItem}
            saving={saving}
            readOnly={readOnly}
            emptyMessage="Nessun modulo configurato"
            addTestId="add-module-btn"
            keyPrefix="module"
            maxQty={999}
            headerAction={
              <Button variant="ghost" size="sm" onClick={loadBOM}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            }
          />

          <ProductSection
            title="Inverter"
            icon={Zap}
            colors={TYPE_COLORS.INVERTER}
            badgeClass="bg-blue-100 text-blue-700 border-0"
            count={calculations.inverterCount}
            typeCodes={INVERTER_TYPE_CODES}
            items={inverterItems}
            onAdd={handleAddItem}
            onUpdateQuantity={handleUpdateQuantity}
            onRemove={handleRemoveItem}
            saving={saving}
            readOnly={readOnly}
            emptyMessage="Nessun inverter configurato"
            addTestId="add-inverter-btn"
            keyPrefix="inverter"
            maxQty={99}
            headerBadge={
              <Badge variant="outline" className="text-xs font-normal text-zinc-500">+ Ibridi</Badge>
            }
          />

          <ProductSection
            title="Sistema di Accumulo"
            icon={Battery}
            colors={TYPE_COLORS.STORAGE}
            badgeClass="bg-emerald-100 text-emerald-700 border-0"
            count={storageEnabled ? calculations.storageCount : 0}
            typeCode="STORAGE"
            items={storageItems}
            onAdd={handleAddItem}
            onUpdateQuantity={handleUpdateQuantity}
            onRemove={handleRemoveItem}
            saving={saving}
            readOnly={readOnly}
            emptyMessage="Nessun sistema di accumulo configurato"
            addTestId="add-storage-btn"
            keyPrefix="storage"
            maxQty={99}
            collapsibleOpen={storageEnabled}
            headerAction={!readOnly ? (
              <div className="flex items-center gap-3">
                <Label htmlFor="storage-toggle" className="text-sm text-zinc-500">
                  {storageEnabled ? 'Attivo' : 'Disattivato'}
                </Label>
                <Switch
                  id="storage-toggle"
                  checked={storageEnabled}
                  onCheckedChange={setStorageEnabled}
                  data-testid="storage-toggle-switch"
                />
              </div>
            ) : null}
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
